import { FC, useCallback, useEffect } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Button, Card, CardBody, Flex, Text, VStack } from '@bonusly/design-system';

import { useLinearAuthorize, useLinearDisconnect, useLinearStatus } from '../../queries/linear';

interface LinearConnectCardProps {
	returnPath?: string;
}

export const LinearConnectCard: FC<LinearConnectCardProps> = ({ returnPath }) => {
	const { data: status, isLoading: isLoadingStatus } = useLinearStatus();
	const { mutateAsync: authorize, isLoading: isAuthorizing } = useLinearAuthorize();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useLinearDisconnect();
	const { track } = useAnalytics();

	const connectionStatus = status?.status;

	useEffect(() => {
		if (connectionStatus === 'connected' && sessionStorage.getItem('bizy_connecting_linear')) {
			track(AnalyticsEvent.Linear.Connected);
			sessionStorage.removeItem('bizy_connecting_linear');
		}
	}, [connectionStatus, track]);

	const handleConnect = useCallback(async () => {
		track(AnalyticsEvent.Linear.ConnectClicked);
		try {
			const { redirectUrl } = await authorize(returnPath ?? window.location.pathname);
			if (redirectUrl) {
				sessionStorage.setItem('bizy_connecting_linear', 'true');
				window.location.href = redirectUrl;
			}
		} catch {
			// Error handled by React Query
		}
	}, [authorize, returnPath, track]);

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Linear.Disconnected);
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, track]);

	if (isLoadingStatus) return null;
	const isConnected = connectionStatus === 'connected';
	const isExpired = connectionStatus === 'expired';

	const statusLabel = isConnected ? 'Connected' : isExpired ? 'Reconnect required' : 'Not connected';
	const statusColor = isExpired ? 'text.warning' : 'text.softer';

	return (
		<Card variant="outline" borderColor="border.soft" borderRadius="lg">
			<CardBody py={3} px={4}>
				<Flex alignItems="center" justifyContent="space-between">
					<VStack spacing={0} align="flex-start">
						<Text fontWeight="semibold" fontSize="sm" color="text.default">
							Linear
						</Text>
						<Text fontSize="xs" color={statusColor}>
							{statusLabel}
						</Text>
					</VStack>
					{isConnected ? (
						<Button size="sm" variant="ghost" colorScheme="secondary" onClick={handleDisconnect} isLoading={isDisconnecting}>
							Disconnect
						</Button>
					) : (
						<Button size="sm" colorScheme="primary" onClick={handleConnect} isLoading={isAuthorizing}>
							{isExpired ? 'Reconnect' : 'Connect'}
						</Button>
					)}
				</Flex>
			</CardBody>
		</Card>
	);
};
